import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { PublicLayout } from "@/components/PublicLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export const AuthCard = ({ title, sub, children }) => (
  <PublicLayout>
    <div className="relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0"><div className="aurora-layer opacity-60" /></div>
      <div className="relative mx-auto max-w-md px-5 py-14 sm:py-24 fade-up">
        <p className="eyebrow">AetherTags private beta</p>
        <h1 className="mt-4 text-4xl sm:text-5xl leading-[1.02]">{title}</h1>
        <p className="mt-3 text-muted-foreground">{sub}</p>
        <div className="glass hairline-gold mt-10 rounded-[28px] border p-6 sm:p-8">{children}</div>
      </div>
    </div>
  </PublicLayout>
);

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Welcome back" sub="Log in to your AetherTags beta account.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="login-email">Email</Label>
          <Input id="login-email" type="email" required data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="login-password">Password</Label>
          <Input id="login-password" type="password" required data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <p data-testid="login-error-message" className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={busy} data-testid="login-submit-button" className="btn-aurora w-full rounded-2xl h-12">
          {busy ? "Logging in…" : "Log in"}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          Not invited yet? <Link to="/#waitlist" data-testid="login-to-waitlist-link" className="inline-flex min-h-[44px] items-center text-primary underline-offset-4 hover:underline">Join the waitlist</Link>
        </p>
      </form>
    </AuthCard>
  );
}
